import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import {
  Box,
  Button,
  Card,
  Divider,
  Grid,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';
import { TemplatePreview, type PreviewPayload } from '../../components/inbox/template-preview';
import {
  BuilderSwitch,
  KIND_META,
  type TemplateKind,
} from '../../components/templates/messenger-template-builders';

const KINDS = Object.keys(KIND_META) as TemplateKind[];

/** WhatsApp-style full-page builder: pick a type (the form manages itself),
 *  fill it in, and watch the live preview on the right. */
function NewMessengerTemplate() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [kind, setKind] = useState<TemplateKind>('quick_replies');
  const [name, setName] = useState('');
  const [nameTouched, setNameTouched] = useState(false);
  const [preview, setPreview] = useState<PreviewPayload>({});

  const create = trpc.facebook.templates.create.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Template saved', color: 'green' });
      void utils.facebook.templates.list.invalidate();
      navigate({ to: '/vendor/facebook/templates' });
    },
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });

  const nameError = nameTouched && !name.trim() ? 'Give the template a name' : undefined;

  function handleSave(payload: unknown) {
    if (!name.trim()) {
      setNameTouched(true);
      notifications.show({ message: 'Give the template a name', color: 'yellow' });
      return;
    }
    create.mutate({ kind, name: name.trim(), payload } as Parameters<typeof create.mutate>[0]);
  }

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group>
          <Button
            variant="subtle"
            component={Link}
            to="/vendor/facebook/templates"
            leftSection={<IconArrowLeft size={14} />}
          >
            Messenger templates
          </Button>
        </Group>
        <Title order={2}>New Messenger template</Title>

        <Grid gap="md">
          <Grid.Col span={{ base: 12, md: 7 }}>
            <Card>
              <Stack gap="sm">
                <Group grow>
                  <TextInput
                    label="Template name"
                    placeholder="Welcome menu"
                    value={name}
                    error={nameError}
                    onChange={(e) => setName(e.currentTarget.value)}
                    onBlur={() => setNameTouched(true)}
                  />
                  <Select
                    label="Template type"
                    description={KIND_META[kind].hint}
                    allowDeselect={false}
                    data={KINDS.map((k) => ({ value: k, label: KIND_META[k].label }))}
                    value={kind}
                    onChange={(v) => {
                      setKind((v as TemplateKind) ?? 'quick_replies');
                      setPreview({});
                    }}
                  />
                </Group>
                <Divider />
                <BuilderSwitch
                  key={kind}
                  kind={kind}
                  sending={create.isPending}
                  submitLabel="Create template"
                  onChange={setPreview}
                  onSave={handleSave}
                />
              </Stack>
            </Card>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Stack gap="xs" pos="sticky" top={80}>
              <Text size="sm" fw={500}>
                Live preview
              </Text>
              <Box>
                <TemplatePreview kind={kind} payload={preview} channel="messenger" />
              </Box>
            </Stack>
          </Grid.Col>
        </Grid>
      </Stack>
    </motion.div>
  );
}

export const Route = createFileRoute('/vendor/facebook/templates/new')({
  component: NewMessengerTemplate,
});
